/**
 * @file React auth context: cookie-based session bootstrap, idle logout,
 * proactive access-token refresh, and role/feature helpers for the SPA.
 * Tokens are not stored in localStorage; session lives in HttpOnly cookies via the API client.
 * @module frontend/src/contexts/AuthContext
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as authApi from '../api/auth';
import { ensureFreshSession, setAuthExpiredHandler } from '../api/client';

const AuthContext = createContext(null);

/** Match backend JWT_REFRESH_EXPIRY default (8h sliding idle). */
const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const IDLE_ACTIVITY_THROTTLE_MS = 1000;
const IDLE_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'focus'];
/** Access JWT defaults to 15m — refresh early so cookies do not vanish mid-action. */
const PROACTIVE_REFRESH_MS = 10 * 60 * 1000;
/** Ignore visibility/focus refresh storms from print dialogs and tab switches. */
const VISIBILITY_REFRESH_DEBOUNCE_MS = 1500;
const MIN_VISIBILITY_REFRESH_GAP_MS = 2 * 60 * 1000;

/**
 * Provides session state and auth actions to the tree under AuthProvider.
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [role, setRole] = useState(null);
  const [features, setFeatures] = useState({ categoryAssigner: false });
  const [config, setConfig] = useState({ allowOpenRegistration: true, googleEnabled: false });
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((session) => {
    if (!session) {
      setUser(null);
      setCompany(null);
      setRole(null);
      setFeatures({ categoryAssigner: false });
      return;
    }
    setUser(session.user || null);
    setCompany(session.company || null);
    setRole(session.role || null);
    setFeatures(session.features || { categoryAssigner: false });
  }, []);

  const handleAuthExpired = useCallback(() => {
    // Clear SPA state only. Do not POST /logout — that revokes a still-valid
    // refresh cookie when refresh failed due to a race, CSRF mismatch, or a
    // response lost while the tab was hidden (print dialog / PDF tab).
    applySession(null);
  }, [applySession]);

  useEffect(() => {
    setAuthExpiredHandler(handleAuthExpired);
    return () => setAuthExpiredHandler(null);
  }, [handleAuthExpired]);

  /** Re-fetch /auth/me and apply session. Only 401 clears the SPA session. */
  const refresh = useCallback(async () => {
    try {
      const session = await authApi.getMe();
      applySession(session);
      return session;
    } catch (err) {
      const status = err?.statusCode || err?.response?.status;
      if (status === 401) applySession(null);
      return null;
    }
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await authApi.getAuthConfig();
        if (!cancelled) setConfig(cfg);
      } catch {
        /* ignore — config is optional; API may be down */
      }
      if (cancelled) return;
      try {
        const session = await authApi.refreshSession();
        if (!cancelled) applySession(session);
      } catch {
        try {
          const session = await authApi.getMe();
          if (!cancelled) applySession(session);
        } catch {
          if (!cancelled) applySession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const login = useCallback(
    async (email, password) => {
      const session = await authApi.login({ email, password });
      applySession(session);
      return session;
    },
    [applySession]
  );

  const register = useCallback(
    async (payload) => {
      const session = await authApi.register(payload);
      applySession(session);
      return session;
    },
    [applySession]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      applySession(null);
    }
  }, [applySession]);

  // Log out after sliding idle with no user interaction (aligned with refresh token lifetime)
  // REVIEW [LOW]: Client-only idle timer; server refresh-token TTL is the real enforcement.
  useEffect(() => {
    if (!user) return undefined;

    let idleTimer;
    let lastActivityAt = 0;

    const scheduleLogout = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Soft expiry — clear session; ProtectedRoute navigates to login with returnUrl
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityAt < IDLE_ACTIVITY_THROTTLE_MS) return;
      lastActivityAt = now;
      scheduleLogout();
    };

    IDLE_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') onActivity();
    };
    document.addEventListener('visibilitychange', onVisible);
    scheduleLogout();

    return () => {
      clearTimeout(idleTimer);
      IDLE_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, logout]);

  // Keep access cookie fresh while the user is signed in (avoids mid-click 401s).
  // Visibility/focus refreshes are debounced and never force logout — print dialogs
  // and PDF tabs hide this page for a moment; a failed background refresh must not
  // bounce the user to login. User-facing 401s still go through the API interceptor.
  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    let visibilityTimer;
    let lastRefreshAt = Date.now();

    const refreshQuietly = async () => {
      try {
        const result = await ensureFreshSession();
        if (cancelled) return;
        if (result === 'ok') lastRefreshAt = Date.now();
        // Never force logout from a background refresh. A 401 on a real API
        // call still goes through the interceptor after a failed refresh.
      } catch {
        /* transient failures are retried on the next tick / next API call */
      }
    };

    const intervalId = setInterval(refreshQuietly, PROACTIVE_REFRESH_MS);

    const onVisibleAgain = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      clearTimeout(visibilityTimer);
      visibilityTimer = setTimeout(() => {
        if (cancelled) return;
        if (Date.now() - lastRefreshAt < MIN_VISIBILITY_REFRESH_GAP_MS) return;
        refreshQuietly();
      }, VISIBILITY_REFRESH_DEBOUNCE_MS);
    };

    document.addEventListener('visibilitychange', onVisibleAgain);
    window.addEventListener('focus', onVisibleAgain);
    window.addEventListener('pageshow', onVisibleAgain);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(visibilityTimer);
      document.removeEventListener('visibilitychange', onVisibleAgain);
      window.removeEventListener('focus', onVisibleAgain);
      window.removeEventListener('pageshow', onVisibleAgain);
    };
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      company,
      role,
      features,
      config,
      loading,
      isAuthenticated: Boolean(user),
      isPlatformAdmin: Boolean(user?.isPlatformAdmin),
      canManageGroups: role === 'admin' || role === 'manager' || Boolean(user?.isPlatformAdmin),
      canManageUsers: role === 'admin' || role === 'manager' || Boolean(user?.isPlatformAdmin),
      canUseApp: company ? company.canUseApp !== false : Boolean(user?.isPlatformAdmin),
      login,
      register,
      logout,
      refresh,
      applySession,
      setConfig,
    }),
    [user, company, role, features, config, loading, login, register, logout, refresh, applySession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook for auth session, role flags, and login/logout/register helpers.
 * @returns {object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
